// library imports
import {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useContext,
  useReducer,
} from "react";
import {
  theme,
  Button,
  DatePicker,
  Flex,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Modal,
} from "antd";
import {
  DownloadOutlined,
  SearchOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
// import { useForm } from "antd/es/form/Form";
// hook imports
import useFetch from "../../hooks/useFetch";
import { IndexContext } from "../../context/IndexContext";
// config import
import { MQTT_REFRESH_RATE } from "../../configs/configs";
// style imports
import "../../styles/Alarm/AlarmPage.css";

const { Title, Text } = Typography;
const { useToken } = theme;

function reducer(state, action) {
  switch (action.type) {
    case "setStartDate":
      return { ...state, startDate: action.date };
    case "setEndDate":
      return { ...state, endDate: action.date };
    case "setSiteSelected":
      return {
        ...state,
        siteSelected: action.site,
      };
    default:
      return "Unrecognized command";
  }
}

export default function AlarmServicePage() {
  const { token } = useToken();
  const tableWrapperViewportHeight = 1;

  const { notificationApi, messageApi } = useContext(IndexContext);
  const [fetchWrapper] = useFetch();
  // const [form] = useForm();
  // const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedAlarmView, setSelectedAlarmView] = useState("Active");

  // const [deviceList, setDeviceList] = useState([]);
  const [siteList, setSiteList] = useState([]);

  const [activeAlarmFilter, setActiveAlarmFilter] = useState([]);

  const defaultEndDate = dayjs().startOf("day").add(1, "day");
  const [historicalAlarmFilter, setHistoricalAlarmFilter] = useState({
    start_date: defaultEndDate
      .clone()
      .subtract(14, "day")
      .format("YYYY-MM-DD HH:mm:ss"),
    end_date: defaultEndDate.format("YYYY-MM-DD HH:mm:ss"),
    site_list: [],
  });
  const initialState = {
    startDate: defaultEndDate.clone().subtract(14, "day"),
    endDate: defaultEndDate,
    siteSelected: [],
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const [firstLoadActiveAlarm, setFirstLoadActiveAlarm] = useState(true);
  const [firstLoadHistoricalAlarm, setFirstLoadHistoricalAlarm] =
    useState(true);

  // data for active alarm table
  const [activeAlarmData, setActiveAlarmData] = useState([]);
  // data for historical alarm table
  const [historicalAlarmData, setHistoricalAlarmData] = useState([]);
  // data for active service health checks
  const [activeServiceData, setActiveServiceData] = useState([]);
  // data for historical service health checks
  const [historicalServiceData, setHistoricalServiceData] = useState([]);
  // for resizeable table
  const [tableHeight, setTableHeight] = useState(600);
  const filterRef = useRef();

  function resizeTable() {
    const node = filterRef.current;
    if (!node) {
      return;
    }
    const { height } = node.getBoundingClientRect();

    setTableHeight(
      window.innerHeight * tableWrapperViewportHeight - height - 285,
    );
  }

  function handleStartDate(date) {
    dispatch({ type: "setStartDate", date: date });
  }
  function handleEndDate(date) {
    dispatch({ type: "setEndDate", date: date });
  }
  function handleSiteChange(site) {
    dispatch({ type: "setSiteSelected", site: site });
  }

  function getSiteList() {
    fetchWrapper({
      endpoint_url: "alarm_service/getSiteList",
      method: "GET",
      set_content_type_header: false,
      params: {},
      onSuccess: (response) => {
        setSiteList(response.data);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get site list.",
          description: response.statusText,
        });

        setSiteList([]);
      },
    });
  }

  /* API to get active alarm */
  function getSiteActiveAlarm() {
    fetchWrapper({
      endpoint_url: "alarm_service/getActiveSiteIpcStatusAlarmHistory",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_list: activeAlarmFilter,
      },
      onSuccess: (response) => {
        setActiveAlarmData(response.data);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get active alarm.",
          description: response.statusText,
        });
      },
      onFinish: () => {
        setFirstLoadActiveAlarm(false);
      },
    });
  }

  /* API to get historical alarm */
  function getSiteHistoricalAlarm() {
    // New backend endpoint returns an object with `historical_alarm_details`
    fetchWrapper({
      endpoint_url: "alarm_service/getHistoricalSiteIpcStatusAlarmHistory",
      method: "GET",
      set_content_type_header: false,
      params: {
        start_date: historicalAlarmFilter.start_date,
        end_date: historicalAlarmFilter.end_date,
        site_list: historicalAlarmFilter?.site_list ?? state.siteSelected,
      },
      onSuccess: (response) => {
        setHistoricalAlarmData(response.data);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get historical alarm.",
          description: response.statusText,
        });
      },
      onFinish: () => {
        setFirstLoadHistoricalAlarm(false);
      },
    });
  }

  function getActiveService() {
    fetchWrapper({
      endpoint_url: "alarm_service/getActiveServiceHealthCheck",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_list: activeAlarmFilter,
      },
      onSuccess: (response) => {
        setActiveServiceData(response.data);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get active service health checks.",
          description: response.statusText,
        });
      },
      onFinish: () => {},
    });
  }

  function getHistoricalService() {
    fetchWrapper({
      endpoint_url: "alarm_service/getHistoricalServiceHealthCheck",
      method: "GET",
      set_content_type_header: false,
      params: {
        start_date: historicalAlarmFilter.start_date,
        end_date: historicalAlarmFilter.end_date,
        site_list: historicalAlarmFilter?.site_list ?? state.siteSelected,
      },
      onSuccess: (response) => {
        setHistoricalServiceData(response.data);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get historical service health checks.",
          description: response.statusText,
        });
      },
      onFinish: () => {},
    });
  }

  function downloadFile() {
    return new Promise((resolve) => {
      const exportData = (
        selectedAlarmView === "Historical"
          ? [...(historicalAlarmData ?? []), ...(historicalServiceData ?? [])]
          : [...(activeAlarmData ?? []), ...(activeServiceData ?? [])]
      ).sort((a, b) => dayjs(b.start).valueOf() - dayjs(a.start).valueOf());
      fetchWrapper({
        endpoint_url: "alarm_service/downloadFile",
        method: "POST",
        json_output: false,
        body: {
          data: exportData,
          sheet_name:
            selectedAlarmView === "Active"
              ? "Active Alarm"
              : "Historical Alarm",
        },

        onSuccess: (response) => {
          const contentType = response.headers.get("content-type") || "";

          // Backend returned JSON (error response)
          if (contentType.includes("application/json")) {
            response.json().then((data) => {
              console.error("Download failed:", data);

              notificationApi.error({
                message: "Failed to download file",
                description:
                  data?.message || "Unexpected response from server.",
              });

              resolve(false);
            });

            return;
          }

          // Backend returned Excel file
          response.blob().then((blob) => {
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;

            // Try to use filename from backend
            const disposition = response.headers.get("content-disposition");

            let filename =
              selectedAlarmView === "Active"
                ? `Active_Alarm_${dayjs().format("YYYY-MM-DDTHH-mm-ss")}.xlsx`
                : `Historical_Alarm_${dayjs().format(
                    "YYYY-MM-DDTHH-mm-ss",
                  )}.xlsx`;

            if (disposition) {
              const match = disposition.match(/filename="?([^"]+)"?/);

              if (match?.[1]) {
                filename = match[1];
              }
            }

            link.download = filename;

            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);

            window.URL.revokeObjectURL(url);

            resolve(true);
          });
        },

        onResponseError: (response) => {
          console.error("Download error:", response);

          notificationApi.error({
            message: "Failed to download file",
            description: response?.statusText || "Unable to download file.",
          });

          resolve(false);
        },
      });
    });
  }

  useEffect(() => {
    getSiteList();
  }, []);

  useEffect(() => {
    if (selectedAlarmView === "Active") {
      getSiteActiveAlarm();
      getActiveService();

      // refresh data every configurable second
      const timer = setInterval(() => {
        getSiteActiveAlarm();
        getActiveService();
      }, MQTT_REFRESH_RATE * 1000);

      return () => {
        clearInterval(timer);
      };
    }
  }, [selectedAlarmView, activeAlarmFilter]);

  useEffect(() => {
    // Trigger historical alarm fetch when view is Historical.
    // Do not gate on deviceList; backend can return historical alarms without
    // frontend device list being present.
    if (selectedAlarmView === "Historical") {
      getSiteHistoricalAlarm();
      getHistoricalService();
    }
  }, [selectedAlarmView, historicalAlarmFilter]);

  useLayoutEffect(() => {
    resizeTable();
    window.addEventListener("resize", resizeTable);

    return () => {
      window.removeEventListener("resize", resizeTable);
    };
  }, [filterRef]);

  useEffect(() => {
    resizeTable();
  }, [selectedAlarmView]);

  const columns = [
    {
      title: "Site",
      dataIndex: "site",
      width: 250,
    },
    {
      title: "Type",
      dataIndex: "type",
      width: 80,
      filters: [
        {
          text: "IPC",
          value: "IPC",
        },
        {
          text: "SERVICE",
          value: "SERVICE",
        },
      ],
      defaultFilteredValue: ["IPC", "SERVICE"],
      onFilter: (value, record) =>
        record?.type?.toUpperCase().indexOf(value?.toUpperCase()) === 0,
      render: (_, record) => {
        return (
          <Tag>
            <Text style={{ color: "white" }}>{_}</Text>
          </Tag>
        );
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      ellipsis: true,
    },
    {
      title: "Start",
      dataIndex: "start",
      width: 130,
      ellipsis: true,
      sorter: (a, b) => dayjs(a.start).valueOf() - dayjs(b.start).valueOf(),
      defaultSortOrder: "descend",
      render: (start) =>
        start ? dayjs(start).format("YYYY-MM-DD HH:mm:ss") : "-",
    },

    ...(selectedAlarmView === "Historical"
      ? [
          {
            title: "End",
            dataIndex: "end",
            width: 130,
            ellipsis: true,
            render: (end) =>
              end === "-" || !end
                ? "-"
                : dayjs(end).format("YYYY-MM-DD HH:mm:ss"),
          },
        ]
      : []),
  ];

  return (
    <div
      style={{
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <Flex justify="space-between" align="center" className="mb-2">
        <Title level={5} className="m-0">
          {selectedAlarmView} Alarm
        </Title>
        <Segmented
          options={["Active", "Historical"]}
          defaultValue={selectedAlarmView}
          onChange={(value) => {
            setSelectedAlarmView(value);
            setFirstLoadActiveAlarm(true);
            setFirstLoadHistoricalAlarm(true);
          }}
        ></Segmented>
      </Flex>

      <div ref={filterRef}>
        {selectedAlarmView == "Historical" ? (
          <Flex
            className="mb-4"
            gap={8}
            wrap={true}
            align="flex-start"
            justify="space-between"
          >
            <Space.Compact block style={{ width: "fit-content" }}>
              <Button disabled>Site</Button>
              <Select
                placeholder={
                  <Tag color={token.colorPrimary} className="pl-3 pr-3">
                    <Text style={{ color: "white" }}>All</Text>
                  </Tag>
                }
                className="historical-alarm-device-selection w-100"
                value={state.siteSelected}
                options={siteList}
                mode={"multiple"}
                allowClear
                tagRender={({ label, onClose, closable }) => (
                  <Tag
                    color={token.colorPrimary}
                    closable={closable}
                    onClose={onClose}
                    style={{
                      marginRight: 3,
                    }}
                  >
                    <Text style={{ color: "white" }}>{label}</Text>
                  </Tag>
                )}
                onChange={(value) => {
                  handleSiteChange(value);
                }}
              />
            </Space.Compact>

            <Space.Compact block style={{ width: "fit-content" }}>
              <Button disabled>Start</Button>
              <DatePicker
                showTime={{ format: "HH:mm:ss" }}
                allowClear={false}
                value={state.startDate}
                placement="bottomLeft"
                onChange={(date) => {
                  if (!date) return;
                  if (date > state.endDate) {
                    handleStartDate(state.endDate);
                    handleEndDate(date);
                  } else {
                    handleStartDate(date);
                  }
                }}
                disabledDate={(current) => {
                  if (!current) return false;
                  // Disable today and future dates: allow only dates strictly before today
                  const todayStart = dayjs().startOf("day");
                  if (!current.isBefore(todayStart, "day")) return true;

                  // Also disable dates after the selected end date (if any)
                  if (state.endDate && current.isAfter(state.endDate, "day"))
                    return true;

                  return false;
                }}
              />
              <Button disabled>End</Button>
              <DatePicker
                showTime
                showHour
                showMinute
                showSecond={false}
                allowClear={false}
                value={state.endDate}
                defaultValue={state.endDate}
                needConfirm={false}
                onChange={(date) => {
                  if (date < state.startDate) {
                    handleEndDate(state.startDate);
                    handleStartDate(date);
                  } else {
                    handleEndDate(date);
                  }
                }}
                minDate={state.startDate}
                placement="topLeft"
              />
            </Space.Compact>

            <Flex gap={8}>
              <Tooltip title="Reset to default setting">
                <Button
                  icon={<UndoOutlined />}
                  onClick={() => {
                    const resetEnd = dayjs()
                      .hour(0)
                      .minute(0)
                      .second(0)
                      .add(1, "day");
                    const resetStart = resetEnd.clone().subtract(14, "day");

                    handleStartDate(resetStart);
                    handleEndDate(resetEnd);
                    handleSiteChange([]);
                    setHistoricalAlarmFilter({
                      start_date: resetStart.format("YYYY-MM-DD HH:mm:ss"),
                      end_date: resetEnd.format("YYYY-MM-DD HH:mm:ss"),
                      site_list: [],
                    });
                  }}
                />
              </Tooltip>
              <Tooltip title="Search">
                <Button
                  icon={<SearchOutlined />}
                  onClick={() => {
                    setHistoricalAlarmFilter({
                      start_date: state.startDate.format("YYYY-MM-DD HH:mm:ss"),
                      end_date: state.endDate.format("YYYY-MM-DD HH:mm:ss"),
                      site_list: state.siteSelected,
                    });
                  }}
                />
              </Tooltip>

              <Tooltip
                title={
                  historicalAlarmData.length === 0 &&
                  historicalServiceData.length === 0
                    ? "No data to download"
                    : ""
                }
              >
                <Button
                  disabled={
                    historicalAlarmData.length === 0 &&
                    historicalServiceData.length === 0
                  }
                  icon={<DownloadOutlined />}
                  onClick={downloadFile}
                />
              </Tooltip>
            </Flex>
          </Flex>
        ) : (
          <Flex
            className="mb-4"
            gap={8}
            wrap={true}
            align="flex-start"
            justify="space-between"
          >
            <Space.Compact block style={{ width: "fit-content" }}>
              <Button disabled>Site</Button>
              <Select
                placeholder={
                  <Tag color={token.colorPrimary} className="pl-3 pr-3">
                    <Text style={{ color: "white" }}>All</Text>
                  </Tag>
                }
                style={{ width: "calc(100vw - 176px)" }}
                value={state.siteSelected}
                options={siteList}
                mode={"multiple"}
                allowClear
                tagRender={({ label, onClose, closable }) => (
                  <Tag
                    color={token.colorPrimary}
                    closable={closable}
                    onClose={onClose}
                    style={{
                      marginRight: 3,
                    }}
                  >
                    <Text style={{ color: "white" }}>{label}</Text>
                  </Tag>
                )}
                onChange={(value) => {
                  handleSiteChange(value);
                }}
              />
            </Space.Compact>

            <Flex gap={8}>
              <Tooltip title="Search">
                <Button
                  icon={<SearchOutlined />}
                  onClick={() => {
                    // state.siteSelected is an array (mode=multiple). If empty, pass [] to mean All
                    setActiveAlarmFilter(
                      state.siteSelected && state.siteSelected.length > 0
                        ? state.siteSelected
                        : [],
                    );
                  }}
                />
              </Tooltip>

              <Tooltip
                title={
                  activeAlarmData.length === 0 && activeServiceData.length === 0
                    ? "No data to download"
                    : ""
                }
              >
                <Button
                  disabled={
                    activeAlarmData.length === 0 &&
                    activeServiceData.length === 0
                  }
                  icon={<DownloadOutlined />}
                  onClick={downloadFile}
                />
              </Tooltip>
            </Flex>
          </Flex>
        )}
      </div>
      <Table
        // rowKey={(record) => record.key}
        // // onRow={(record, index) => {
        // //   return {
        // //     onDoubleClick: (evt) => {
        // //       onRowClicked(record);
        // //     },
        // //   };
        // // }}
        loading={
          selectedAlarmView === "Active"
            ? firstLoadActiveAlarm
            : selectedAlarmView === "Historical"
              ? firstLoadHistoricalAlarm
              : false
        }
        sticky="true"
        size="small"
        // showHeader

        scroll={{ scrollToFirstRowOnChange: true, x: true, y: tableHeight }}
        pagination={{
          responsive: true,
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: [5, 10, 20, 50, 100],
          position: ["bottomRight"],
          size: "small",
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
        columns={columns}
        dataSource={
          selectedAlarmView === "Historical"
            ? [...(historicalAlarmData ?? []), ...(historicalServiceData ?? [])]
            : [...(activeAlarmData ?? []), ...(activeServiceData ?? [])]
        }
      ></Table>
    </div>
  );
}
